<template>
  <a-card :title="taskName" size="small">
    <a-row :gutter="[16, 16]">
      <a-col :span="24">
        <a-row :gutter="[16, 16]">
          <a-col :span="7">
            <a-select
              v-model:value="formData.subStep"
              class="w-100"
              show-search
              placeholder="Select steps to be completed"
              :disabled="isFetchingSimilarSteps"
              :options="getStepsOptions"
              :filter-option="filterOption"
            />
          </a-col>
          <a-col :span="7">
            <a-select
              v-model:value="formData.mustCompleteSubSteps"
              class="w-100"
              show-search
              mode="multiple"
              placeholder="Select steps to be completed"
              :options="getCompleteStepOptions"
              :max-tag-count="1"
              :disabled="isFetchingSimilarSteps"
              :filter-option="filterOption"
            />
          </a-col>
          <a-col :span="7">
            <a-select
              v-model:value="formData.notCompleteSubSteps"
              class="w-100"
              show-search
              mode="multiple"
              placeholder="Select steps not to be completed"
              :options="getNotCompleteStepOptions"
              :max-tag-count="1"
              :disabled="isFetchingSimilarSteps"
              :filter-option="filterOption"
            />
          </a-col>

          <a-col :span="3">
            <a-button
              type="primary"
              :disabled="isGroupButtonDisable"
              :loading="isSimilarStepGroupLoading"
              @click="handleGroupSimilarSteps"
            >
              Group
            </a-button>
          </a-col>
        </a-row>
      </a-col>
      <a-col :span="24">
        <a-table
          :columns="similarStepsColumns"
          :loading="isFetchingSimilarSteps"
          :data-source="similarStepsData"
        >
          <template #bodyCell="{ record, column, index }">
            <!-- Task Step -->
            <template v-if="column.dataIndex === 'step'">
              <span>{{ record.subStep }}</span>
            </template>

            <!-- Step Must Be Completed -->
            <template v-if="column.dataIndex === 'stepMustCompleted'">
              <a-select
                v-if="isEdit[record.id]"
                v-model:value="isEdit[record.id].mustCompleteSubSteps"
                class="w-100"
                show-search
                mode="multiple"
                placeholder="Select steps to be completed"
                :options="editStepCompleteOptions(record)"
                :filter-option="filterOption"
              />
              <template v-else>
                <template v-if="!record.mustCompleteSubSteps.length">
                  <a-tag color="blue"> N/A </a-tag>
                </template>
                <template
                  v-for="item of record.mustCompleteSubSteps"
                  v-else
                  :key="item"
                >
                  <a-tag color="blue">
                    {{ item }}
                  </a-tag>
                </template>
              </template>
            </template>

            <!-- Step Not Be Completed -->
            <template v-if="column.dataIndex === 'stepNotBeCompleted'">
              <a-select
                v-if="isEdit[record.id]"
                v-model:value="isEdit[record.id].notCompleteSubSteps"
                class="w-100"
                show-search
                mode="multiple"
                placeholder="Select steps not to be completed"
                :options="editStepNotCompleteOptions(record)"
                :filter-option="filterOption"
              />
              <template v-else>
                <template v-if="!record.notCompleteSubSteps.length">
                  <a-tag color="green"> N/A </a-tag>
                </template>
                <template
                  v-for="item of record.notCompleteSubSteps"
                  v-else
                  :key="item"
                >
                  <a-tag color="green">
                    {{ item }}
                  </a-tag>
                </template>
              </template>
            </template>

            <template v-if="column.dataIndex === 'action'">
              <span class="d-flex ml-auto mt-4">
                <div class="editable-row-operations">
                  <span v-if="isEdit[record.id]">
                    <a-space>
                      <a-button
                        type="primary"
                        :loading="isStepsUpdating[record.id]"
                        @click="handleSave(record)"
                      >
                        <template #icon> <check-outlined /> </template>
                      </a-button>
                      <a-button
                        type="danger"
                        :disabled="isStepsUpdating[record.id]"
                        @click="handleCancel(record)"
                      >
                        <template #icon> <close-outlined /></template>
                      </a-button>
                    </a-space>
                  </span>
                  <span v-else>
                    <a-button id="edit-task-btn" @click="handleEdit(record)">
                      <template #icon>
                        <form-outlined class="text-light" />
                      </template>
                    </a-button>
                  </span>
                </div>

                <!-- Delete -->
                <a-popconfirm
                  title="Are you sure delete this similar steps group?"
                  ok-text="Yes"
                  cancel-text="No"
                  :disabled="isStepsUpdating[record.id]"
                  @confirm="deleteSimilarStepsGroup(record.id)"
                >
                  <a-button
                    type="danger"
                    class="ml-2"
                    :disabled="isStepsUpdating[record.id]"
                  >
                    <template #icon>
                      <delete-outlined />
                    </template>
                  </a-button>
                </a-popconfirm>
              </span>
            </template>
          </template>
        </a-table>
      </a-col>
    </a-row>
  </a-card>
  <div style="display: flex; justify-content: flex-end">
    <a-button type="primary" @click="handleBackClick"> Back </a-button>
  </div>
</template>

<script>
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  FormOutlined,
} from '@ant-design/icons-vue';
import SimilarStepsService from 'src/services/similarSteps';
import { mapGetters } from 'vuex';
import { similarStepsColumns } from './config';

export default {
  components: {
    FormOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
  },

  inject: ['toast'],

  props: {
    currentProcess: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

  emits: ['handleBack'],

  setup() {
    return {
      similarStepsColumns,
    };
  },

  data() {
    return {
      formData: {
        subStep: null,
        mustCompleteSubSteps: [],
        notCompleteSubSteps: [],
      },
      similarStepsData: [],
      isEdit: {},
      isStepsUpdating: {},
      isStepsDeleting: {},
      isFetchingSimilarSteps: false,
      isSimilarStepGroupLoading: false,
    };
  },

  computed: {
    ...mapGetters(['selectedTask', 'taskName']),

    getSteps() {
      if (this.currentProcess === null) return [];
      let stepList = [];
      this.currentProcess['steps'].forEach((step) => {
        step['substeps'].forEach((substep) => {
          stepList.push({ value: substep, label: substep });
        });
      });
      return stepList;
    },

    getStepsOptions() {
      const completedSteps = new Set(
        this.similarStepsData.map((item) => item.subStep)
      );

      const incompleteSteps = this.getSteps.filter(
        (item) =>
          !completedSteps.has(item.value) &&
          !this.formData.notCompleteSubSteps.includes(item.value) &&
          !this.formData.mustCompleteSubSteps.includes(item.value)
      );

      return incompleteSteps;
    },

    getCompleteStepOptions() {
      const steps = this.getSteps.filter(
        (item) => item.value !== this.formData.subStep
      );
      return steps.filter(
        (item) =>
          !this.formData.notCompleteSubSteps.some(
            (element) => element === item.value
          )
      );
    },

    getNotCompleteStepOptions() {
      const steps = this.getSteps.filter(
        (item) => item.value !== this.formData.subStep
      );
      return steps.filter(
        (item) =>
          !this.formData.mustCompleteSubSteps.some(
            (element) => element === item.value
          )
      );
    },

    isGroupButtonDisable() {
      return (
        this.formData.subStep === null ||
        !(
          this.formData.mustCompleteSubSteps.length ||
          this.formData.notCompleteSubSteps.length
        )
      );
    },
  },

  async mounted() {
    await this.fetchSimilarSteps();
  },

  methods: {
    filterOption(input, option) {
      return option.label.toLowerCase().startsWith(input.toLowerCase());
    },

    async fetchSimilarSteps() {
      this.isFetchingSimilarSteps = true;
      const [error, data] =
        await SimilarStepsService.fetchSimilarStepsByTaskAndProcess(
          this.selectedTask,
          this.currentProcess['name']
        );
      this.isFetchingSimilarSteps = false;
      if (error) return this.toast.error('Failed to fetch similar steps.');
      this.parseData(data);
    },

    addItemInSimilarStepData(item) {
      const payload = {
        id: item.id,
        task: item.task,
        process: item.process,
        subStep: item.sub_step,
        mustCompleteSubSteps: item.must_complete_sub_steps,
        notCompleteSubSteps: item.not_complete_sub_steps,
      };
      this.similarStepsData.push(payload);
    },

    parseData(data) {
      data.forEach((item) => {
        this.addItemInSimilarStepData(item);
      });
    },

    async deleteSimilarStepsGroup(id) {
      this.isStepsDeleting[id] = true;
      const [error] = await SimilarStepsService.deleteSimilarStep(id);
      delete this.isStepsDeleting[id];
      if (error) return this.toast.error('Failed to delete similar step.');
      this.similarStepsData = this.similarStepsData.filter(
        (item) => item.id !== id
      );
      this.handleCancel();
      this.toast.success('Similar step deleted successfully.');
    },

    editStepCompleteOptions(record) {
      const stepObject = this.isEdit[record.id];
      const steps = this.getSteps.filter(
        (item) => item.value !== stepObject.subStep
      );
      return steps.filter(
        (item) =>
          !stepObject.notCompleteSubSteps.some(
            (element) => element === item.value
          )
      );
    },

    editStepNotCompleteOptions(record) {
      const stepObject = this.isEdit[record.id];
      const steps = this.getSteps.filter(
        (item) => item.value !== stepObject.subStep
      );
      return steps.filter(
        (item) =>
          !stepObject.mustCompleteSubSteps.some(
            (element) => element === item.value
          )
      );
    },

    async handleSave(record) {
      this.isStepsUpdating[record.id] = true;
      const payload = {
        must_complete_sub_steps: this.isEdit[record.id].mustCompleteSubSteps,
        not_complete_sub_steps: this.isEdit[record.id].notCompleteSubSteps,
      };

      const [error] = await SimilarStepsService.updateSimilarStep(
        record.id,
        payload
      );
      this.isStepsUpdating[record.id] = false;
      if (error) return this.toast.error('Failed to update similar step.');

      this.toast.success('Similar step updated successfully.');
      this.updateSimilarStepData(record);
    },

    updateSimilarStepData(record) {
      const index = this.similarStepsData.findIndex(
        (item) => item.id === record.id
      );
      if (index < 0) return;
      this.similarStepsData[index] = JSON.parse(
        JSON.stringify(this.isEdit[record.id])
      );
      this.handleCancel(record);
    },

    handleCancel(record) {
      delete this.isEdit[record.id];
      delete this.isStepsUpdating[record.id];
    },

    handleEdit(record) {
      this.isEdit[record.id] = {};
      this.isEdit[record.id] = { ...record };
    },

    createSimilarStepGroupPayload() {
      return {
        task: this.selectedTask,
        process: this.currentProcess['name'],
        sub_step: this.formData.subStep,
        must_complete_sub_steps: this.formData.mustCompleteSubSteps,
        not_complete_sub_steps: this.formData.notCompleteSubSteps,
      };
    },

    async handleGroupSimilarSteps() {
      this.isSimilarStepGroupLoading = true;
      const [error, data] = await SimilarStepsService.createSimilarStep(
        this.createSimilarStepGroupPayload()
      );
      this.isSimilarStepGroupLoading = false;
      if (error) return this.toast.error('Failed to create similar step.');
      this.addItemInSimilarStepData(data);
      this.resetFormData();
      this.toast.success('Similar steps group created successfully.');
    },

    resetFormData() {
      this.formData.subStep = null;
      this.formData.mustCompleteSubSteps = [];
      this.formData.notCompleteSubSteps = [];
    },

    handleBackClick() {
      this.$emit('handleBack');
    },
  },
};
</script>
